import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import getExchangeRate from "../apis/currency/api";
import options from "../models/options.json";

const DEFAULT_CURRENCY = "inr";
const CURRENCY_OPTIONS = options.currencyOptions || [];

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const selectedCurrency = useMemo(() => (
    CURRENCY_OPTIONS.find((option) => option.id === currency) ||
    CURRENCY_OPTIONS.find((option) => option.id === DEFAULT_CURRENCY)
  ), [currency]);

  const usdFallbackRate = useMemo(() => (
    CURRENCY_OPTIONS.find((option) => option.id === "usd")?.exchangeRate || 1
  ), []);
  const [usdExchangeRate, setUsdExchangeRate] = useState(usdFallbackRate);

  useEffect(() => {
    let isMounted = true;

    const fetchUsdExchangeRate = async () => {
      try {
        const latestRate = await getExchangeRate({ base: "usd", target: "inr", amount: 1 });
        if (isMounted && Number.isFinite(latestRate) && latestRate > 0) {
          setUsdExchangeRate(latestRate);
        }
      } catch (error) {
        console.error("Unable to fetch USD exchange rate", error);
      }
    };

    fetchUsdExchangeRate();

    return () => {
      isMounted = false;
    };
  }, []);

  const currencyCode = selectedCurrency?.label || "INR";
  const exchangeRate = currency === "usd" ? usdExchangeRate : (selectedCurrency?.exchangeRate || 1);
  const locale = currency === "usd" ? "en-US" : "en-IN";

  const formatPrice = useCallback((value) => {
    const convertedValue = Number(value || 0) / exchangeRate;

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(convertedValue);
  }, [currencyCode, exchangeRate, locale]);

  const formatUsdPrice = useCallback((value) => {
    const usdValue = Number(value || 0);
    const convertedValue = currency === "usd" ? usdValue : (usdValue * usdExchangeRate) / exchangeRate;

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(convertedValue);
  }, [currency, currencyCode, exchangeRate, locale, usdExchangeRate]);

  const contextValue = useMemo(() => ({ currency, currencyCode, selectedCurrency, exchangeRate, setCurrency, formatPrice, formatUsdPrice }), [currency, currencyCode, selectedCurrency, exchangeRate, formatPrice, formatUsdPrice]);

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);

export default CurrencyProvider;